import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  Send,
  CheckCircle,
  Clock,
  Eye,
  ArrowRight,
  XCircle,
  Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import DashboardLayout from '../components/DashboardLayout';
import StatusProgressBar from '../components/StatusProgressBar';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';

const PLATFORM_ICONS = {
  facebook: '📘',
  instagram: '📸',
  twitter: '🐦',
  linkedin: '💼',
  tiktok: '🎵',
  youtube: '📺',
  pinterest: '📌',
  threads: '🧵',
  bluesky: '🦋'
};

const STATUS_STYLES = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
  pending_approval: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending Approval' },
  approved: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Approved' },
  scheduled: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Scheduled' },
  published: { bg: 'bg-green-100', text: 'text-green-700', label: 'Published' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' }
};

const PostsPage = () => {
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [calendarFilter, setCalendarFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postsRes, calendarsRes] = await Promise.all([
        api.get('/posts'),
        api.get('/calendars')
      ]);
      setPosts(postsRes.data);
      setCalendars(calendarsRes.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (postId) => {
    try {
      const response = await api.post(`/posts/${postId}/duplicate`);
      setPosts([...posts, response.data]);
      toast.success('Post duplicated');
    } catch (error) {
      toast.error('Failed to duplicate post');
    }
  };

  const handleDelete = async () => {
    if (!selectedPost) return;
    try {
      await api.delete(`/posts/${selectedPost.post_id}`);
      setPosts(posts.filter(p => p.post_id !== selectedPost.post_id));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedPost(null);
    }
  };

  const handleSubmitForApproval = async (postId) => {
    setActionLoading(postId);
    try {
      await api.post(`/posts/${postId}/submit-approval`);
      fetchData();
      toast.success('Post submitted for approval');
    } catch (error) {
      toast.error('Failed to submit for approval');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePublish = async (postId) => {
    setActionLoading(postId);
    try {
      const res = await api.post(`/posts/${postId}/publish`);
      fetchData();
      const platformData = res.data.platform_data || {};
      const successPlatforms = Object.entries(platformData)
        .filter(([_, d]) => d.published)
        .map(([p, d]) => `${p}${d.method === 'api' ? ' (live)' : ' (demo)'}`);
      const failedPlatforms = Object.entries(platformData)
        .filter(([_, d]) => !d.published)
        .map(([p]) => p);

      if (successPlatforms.length > 0) {
        toast.success(`Published to: ${successPlatforms.join(', ')}`);
      }
      if (failedPlatforms.length > 0) {
        toast.error(`Failed on: ${failedPlatforms.join(', ')}`);
      }
      if (successPlatforms.length === 0 && failedPlatforms.length === 0) {
        toast.success('Post published');
      }
    } catch (error) {
      toast.error('Failed to publish post');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (postId) => {
    setActionLoading(postId);
    try {
      await api.post(`/posts/${postId}/approve`);
      fetchData();
      toast.success('Post approved');
    } catch (error) {
      toast.error('Failed to approve post');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (postId) => {
    setActionLoading(postId);
    try {
      await api.post(`/posts/${postId}/reject`);
      fetchData();
      toast.success('Post rejected');
    } catch (error) {
      toast.error('Failed to reject post');
    } finally {
      setActionLoading(null);
    }
  };

  const getQuickAction = (post) => {
    const isLoading = actionLoading === post.post_id;
    
    switch (post.status) {
      case 'draft':
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.preventDefault(); handleSubmitForApproval(post.post_id); }}
            disabled={isLoading}
            className="text-amber-600 border-amber-200 hover:bg-amber-50"
            data-testid={`submit-approval-${post.post_id}`}
          >
            {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3 mr-1" />}
            Submit
          </Button>
        );
      case 'pending_approval':
        return (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.preventDefault(); handleApprove(post.post_id); }}
              disabled={isLoading}
              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
              data-testid={`approve-${post.post_id}`}
            >
              {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.preventDefault(); handleReject(post.post_id); }}
              disabled={isLoading}
              className="text-red-600 border-red-200 hover:bg-red-50"
              data-testid={`reject-${post.post_id}`}
            >
              <XCircle className="h-3 w-3" />
            </Button>
          </div>
        );
      case 'approved':
      case 'scheduled':
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => { e.preventDefault(); handlePublish(post.post_id); }}
            disabled={isLoading}
            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
            data-testid={`publish-${post.post_id}`}
          >
            {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
            Publish
          </Button>
        );
      case 'published':
        return (
          <Badge className="bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3 mr-1" />
            Live
          </Badge>
        );
      default:
        return null;
    }
  };

  const filteredPosts = posts.filter(post => {
    if (statusFilter !== 'all' && post.status !== statusFilter) return false;
    if (calendarFilter !== 'all' && post.calendar_id !== calendarFilter) return false;
    if (platformFilter !== 'all' && !post.platforms.includes(platformFilter)) return false;
    if (searchQuery && !post.content.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const getCalendarName = (calendarId) => {
    return calendars.find(c => c.calendar_id === calendarId)?.name || 'Unknown';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="posts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Posts</h1>
            <p className="text-slate-600 mt-1">{filteredPosts.length} posts</p>
          </div>
          <Link to="/posts/new">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="create-post-btn">
              <Plus className="h-4 w-4 mr-2" />
              Create Post
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <Card className="border-slate-100 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search posts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-posts-input"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
              <Select value={calendarFilter} onValueChange={setCalendarFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Calendar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Calendars</SelectItem>
                  {calendars.map((cal) => (
                    <SelectItem key={cal.calendar_id} value={cal.calendar_id}>
                      {cal.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={platformFilter} onValueChange={setPlatformFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="twitter">Twitter/X</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Posts List */}
        {filteredPosts.length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="h-16 w-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No posts found</h3>
              <p className="text-slate-500 text-center max-w-sm mb-6">
                {searchQuery || statusFilter !== 'all' || calendarFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first post to get started'}
              </p>
              <Link to="/posts/new">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Post
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredPosts.map((post, i) => (
              <motion.div
                key={post.post_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card className="border-slate-100 hover:border-emerald-200 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge className={`${STATUS_STYLES[post.status].bg} ${STATUS_STYLES[post.status].text}`}>
                            {STATUS_STYLES[post.status].label}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {getCalendarName(post.calendar_id)}
                          </span>
                          {post.scheduled_at && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(parseISO(post.scheduled_at), 'MMM d, h:mm a')}
                            </span>
                          )}
                        </div>
                        <Link to={`/posts/${post.post_id}`}>
                          <p className="text-slate-900 hover:text-emerald-600 transition-colors line-clamp-2">
                            {post.content}
                          </p>
                        </Link>
                        <div className="flex items-center gap-3 mt-3">
                          <span className="text-sm">
                            {post.platforms.map(p => PLATFORM_ICONS[p] || '📱').join(' ')}
                          </span>
                          {post.hashtags?.length > 0 && (
                            <span className="text-xs text-slate-500">
                              {post.hashtags.length} hashtags
                            </span>
                          )}
                          {post.media_urls?.length > 0 && (
                            <span className="text-xs text-slate-500">
                              {post.media_urls.length} media
                            </span>
                          )}
                        </div>
                        
                        {/* Mini Status Progress (for non-published posts) */}
                        {post.status !== 'published' && (
                          <div className="mt-3 max-w-xs">
                            <StatusProgressBar status={post.status} size="small" showLabels={false} />
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Quick Action Button */}
                        {getQuickAction(post)}
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/posts/${post.post_id}`} className="flex items-center">
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/posts/${post.post_id}/edit`} className="flex items-center">
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(post.post_id)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedPost(post);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Post</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this post? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default PostsPage;
